import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "@i18n";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import UserNameTagComponent from "../../../cubeComponent/html/UserNameTagComponent";
import IconComponent from "@CubeComponents/icons";
import { usePortalChat, messageTime } from "./context";
import { VirtualizedList } from "./VirtualizedList";
import { ConversationAvatar } from "./ConversationAvatar";
import {
  ATTACHMENT_ROW_HEIGHT,
  FORWARD_HEADER_ROW_HEIGHT,
  FORWARD_ROW_HEIGHT,
  MESSAGE_TEXT_MAX_LENGTH,
  RECEIPT_ROW_HEIGHT,
  SEARCH_MAX_LENGTH,
} from "../portalChat.constants";
import { toObjectIdString } from "@Helpers/schemas/common.schema";
import {
  getMessageReceiptsRequest,
  sendMessageRequest,
  type ChatConversation,
  type ChatMessage,
  type GetMessageReceiptsResponseData,
} from "../api/portalChat.api";

type ForwardRow =
  { kind: "header"; key: string; label: string } | { kind: "item"; key: string; conversation: ChatConversation };

/** Forward a message into any other conversation, with an optional note above the card (PRD §4.4). */
export function ForwardDialog({ message, onClose }: { readonly message: ChatMessage; readonly onClose: () => void }) {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const [search, setSearch] = useState("");
  const [note, setNote] = useState("");
  const [targetId, setTargetId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  // Grouped by type, most recent first inside each group; project chats subtitle their project.
  const rows = useMemo((): ForwardRow[] => {
    const lower = search.trim().toLowerCase();
    const matches = chat.conversations.filter(
      (c) => !lower || c.name.toLowerCase().includes(lower) || c.projectName?.toLowerCase().includes(lower),
    );
    const result: ForwardRow[] = [];
    for (const type of ["direct", "group", "space"] as const) {
      const items = matches
        .filter((c) => c.type === type)
        .sort((a, b) => (b.lastMessageDate ?? b.createdDate) - (a.lastMessageDate ?? a.createdDate));
      if (!items.length) continue;
      result.push({
        kind: "header",
        key: `header-${type}`,
        label: type === "direct" ? t("Direct Chats") : type === "group" ? t("Group Chats") : t("Project Chats"),
      });
      for (const conversation of items) result.push({ kind: "item", key: conversation._id, conversation });
    }
    return result;
  }, [chat.conversations, search, t]);

  const subtitleOf = (conversation: ChatConversation) =>
    conversation.type === "direct"
      ? (conversation.otherUser?.designation ?? t("Direct chat"))
      : conversation.type === "group"
        ? t("Group chat")
        : (conversation.projectName ?? t("Project chat"));

  const send = async () => {
    if (!targetId || sending) return;
    setSending(true);
    try {
      const response = await sendMessageRequest({
        conversationId: toObjectIdString(targetId),
        forwardOfMessageId: toObjectIdString(message._id),
        ...(note.trim() ? { text: note.trim() } : {}),
      });
      if (response.data.status) {
        chat.showToast(t("Message forwarded"));
        await chat.refreshList();
        // PRD §4.4: forwarding takes the user to the destination chat, un-hiding it if necessary.
        chat.unhideAndOpen(targetId);
        onClose();
      } else {
        chat.showServerError(response.data.errorMessage, t("Could not forward the message"));
      }
    } finally {
      setSending(false);
    }
  };

  const previewText = message.text || message.attachments?.[0]?.attachmentsData.name || t("Attachment");

  return (
    <ModalStateDialogComponent
      isVisible
      width="30rem"
      headerTitle={t("Forward this message")}
      onCancel={onClose}
      modelContent={
        <div className="flex w-full flex-col gap-2.5">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, MESSAGE_TEXT_MAX_LENGTH))}
            maxLength={MESSAGE_TEXT_MAX_LENGTH}
            placeholder={t("Add a message if you like.")}
            rows={3}
            className="w-full resize-none rounded-lg border border-[#DDE3E8] px-3 py-2 text-[0.84375rem] leading-relaxed text-[#1C2228] outline-hidden focus:border-[#4794BA]"
          />
          <div className="relative">
            <span className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2">
              <IconComponent name="SearchIcon" width="0.9rem" height="0.9rem" />
            </span>
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              maxLength={SEARCH_MAX_LENGTH}
              placeholder={t("Search for a chat or person")}
              className="h-10 w-full rounded-lg border border-[#4794BA] ps-9 pe-3 text-[0.84375rem] text-[#1C2228] outline-hidden"
            />
          </div>
          <div className="h-64">
            {rows.length > 0 && (
              <VirtualizedList
                items={rows}
                getKey={(row) => row.key}
                rowHeight={(row) => (row.kind === "header" ? FORWARD_HEADER_ROW_HEIGHT : FORWARD_ROW_HEIGHT)}
                ariaLabel={t("Search for a chat or person")}
                renderRow={(row) => {
                  if (row.kind === "header") {
                    return (
                      <div className="flex h-full items-center gap-2 px-1">
                        <span className="text-[0.65rem] font-bold uppercase tracking-[0.11em] text-[#9AA5AD]">
                          {row.label}
                        </span>
                        <span className="h-px flex-1 bg-[#EEF2F5]" />
                      </div>
                    );
                  }
                  const selected = targetId === row.conversation._id;
                  return (
                    <div className="h-full py-0.5">
                      <button
                        type="button"
                        onClick={() => setTargetId(row.conversation._id)}
                        className={`flex h-full w-full items-center gap-2.5 rounded-lg border px-2.5 text-start hover:bg-[#F7F9FB] ${
                          selected ? "border-[#6BBBE3] bg-[#F4FAFD]" : "border-transparent"
                        }`}
                      >
                        <ConversationAvatar conversation={row.conversation} size="small" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[0.84375rem] font-semibold text-[#1C2228]">
                            {row.conversation.name}
                          </span>
                          <span className="block truncate text-[0.7rem] text-[#6B757D]">
                            {subtitleOf(row.conversation)}
                          </span>
                        </span>
                        {selected && <IconComponent name="TickMark" width="1rem" height="1rem" />}
                      </button>
                    </div>
                  );
                }}
              />
            )}
            {!rows.length && (
              <div className="p-6 text-center text-sm text-[#9AA5AD]">{t("No chats match that search")}</div>
            )}
          </div>
          <div className="rounded-lg border border-[#EEF2F5] border-s-2 border-s-[#6BBBE3] bg-[#F7F9FB] px-3 py-2">
            <div className="text-[0.625rem] font-bold uppercase tracking-[0.1em] text-[#9AA5AD]">{t("Forwarding")}</div>
            <div className="mt-0.5 text-xs text-[#1C2228]">
              <span className="font-semibold">{message.senderName}: </span>
              <span className="line-clamp-2">{previewText}</span>
            </div>
          </div>
        </div>
      }
      footerContent={
        <div className="flex gap-2.5">
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            onClick={() => void send()}
            disabled={!targetId || sending}
          >
            {t("Forward")}
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={onClose}>
            {t("Cancel")}
          </ButtonComponent>
        </div>
      }
    />
  );
}

/**
 * Edit-message pop-up (PRD §4.15): pre-filled with the message's CURRENT text, shows the original
 * text and timestamp for reference (and the quoted reply context when the message was a reply),
 * Save disabled while empty, Enter saves, Escape discards. Fully independent of the composer draft.
 */
export function EditMessageDialog({
  message,
  onClose,
}: {
  readonly message: ChatMessage;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const [text, setText] = useState(message.text ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const trimmed = text.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    const ok = await chat.editMessage(message._id, trimmed);
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <ModalStateDialogComponent
      isVisible
      width="28rem"
      headerTitle={t("Edit message")}
      onCancel={onClose}
      modelContent={
        <div className="flex w-full flex-col gap-2">
          {message.replyTo && (
            <div className="rounded-md border-s-2 border-[#4794BA] bg-[#F4FAFD] px-2.5 py-1.5 text-xs">
              <div className="font-bold text-[#1F5F80]">{message.replyTo.senderName}</div>
              <div className="truncate text-[#6B757D]">{message.replyTo.text || t("Attachment")}</div>
            </div>
          )}
          <textarea
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MESSAGE_TEXT_MAX_LENGTH))}
            maxLength={MESSAGE_TEXT_MAX_LENGTH}
            rows={4}
            className="w-full resize-none rounded-lg border border-[#DDE3E8] px-3 py-2 text-[0.84375rem] text-[#1C2228] outline-hidden focus:border-[#4794BA]"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void save();
              }
              if (e.key === "Escape") onClose();
            }}
          />
          <div className="text-xs text-[#9AA5AD]">
            {t("Sent")} {new Date(message.createdDate).toLocaleString()}
            {message.editedDate ? ` · ${t("edited")}` : ""}
          </div>
        </div>
      }
      footerContent={
        <div className="flex gap-2.5">
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            disabled={!text.trim() || saving}
            onClick={() => void save()}
          >
            {t("Save")}
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={onClose}>
            {t("Cancel")}
          </ButtonComponent>
        </div>
      }
    />
  );
}

/** Flat rows for the virtualized receipts list — a space chat can have thousands of members. */
type ReceiptRow =
  | { kind: "header"; key: string; label: string; read: boolean }
  | { kind: "empty"; key: string }
  | {
      kind: "reader";
      key: string;
      userId: string;
      fullName: string;
      designation: string | null;
      lastReadDate?: number | undefined;
    };

/** "Message details" for an OWN message: the bubble, sent by/on, then who has read it (PRD). */
export function ReceiptsDialog({ message, onClose }: { readonly message: ChatMessage; readonly onClose: () => void }) {
  const { t } = useTranslation();
  const [data, setData] = useState<GetMessageReceiptsResponseData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // Fire-and-forget: the `cancelled` flag is the only cleanup this effect needs.
    void getMessageReceiptsRequest({ messageId: toObjectIdString(message._id) })
      .then((response) => {
        if (!cancelled && response.data.status) {
          setData({
            message: response.data.message,
            readBy: response.data.readBy ?? [],
            unreadBy: response.data.unreadBy ?? [],
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [message._id]);

  const receiptRows = useMemo((): ReceiptRow[] => {
    if (!data) return [];
    const rows: ReceiptRow[] = [
      { kind: "header", key: "read", label: t("Read by {{count}}", { count: data.readBy.length }), read: true },
    ];
    for (const reader of data.readBy) {
      rows.push({
        kind: "reader",
        key: `read-${String(reader._id)}`,
        userId: String(reader._id),
        fullName: reader.fullName,
        designation: reader.designation ?? null,
        lastReadDate: reader.lastReadDate,
      });
    }
    if (!data.readBy.length) rows.push({ kind: "empty", key: "read-empty" });
    if (data.unreadBy.length) {
      rows.push({
        kind: "header",
        key: "unread",
        label: t("Not read yet {{count}}", { count: data.unreadBy.length }),
        read: false,
      });
      for (const member of data.unreadBy) {
        rows.push({
          kind: "reader",
          key: `unread-${String(member._id)}`,
          userId: String(member._id),
          fullName: member.fullName,
          designation: member.designation ?? null,
        });
      }
    }
    return rows;
  }, [data, t]);

  const bubbleText = message.text || message.attachments?.[0]?.attachmentsData.name || t("Attachment");

  return (
    <ModalStateDialogComponent
      isVisible
      width="26.25rem"
      headerTitle={t("Message details")}
      onCancel={onClose}
      footerContent={<></>}
      modelContent={
        // One scroll region only: the summary is fixed, the reader list scrolls inside its own box.
        <div className="flex w-full flex-col gap-3 overflow-hidden">
          <div>
            <div className="rounded-[0.625rem] bg-[#4794BA] px-3 py-2.5 text-[0.8125rem] leading-relaxed text-white">
              <span className="line-clamp-4 whitespace-pre-wrap wrap-break-word">{bubbleText}</span>
            </div>
            <div className="mt-3 flex gap-5">
              <div>
                <div className="text-[0.65rem] font-bold uppercase tracking-[0.1em] text-[#9AA5AD]">{t("Sent by")}</div>
                <div className="mt-0.5 text-[0.8125rem] font-semibold text-[#1C2228]">{message.senderName}</div>
              </div>
              <div>
                <div className="text-[0.65rem] font-bold uppercase tracking-[0.1em] text-[#9AA5AD]">{t("Sent on")}</div>
                <div className="mt-0.5 text-[0.8125rem] font-semibold text-[#1C2228]">
                  {new Date(message.createdDate).toLocaleString()}
                </div>
              </div>
            </div>
          </div>
          <div className="h-[40vh] border-t border-[#EEF2F5] pt-2">
            {loading && <div className="text-sm text-[#9AA5AD]">{t("Loading")}…</div>}
            {data && (
              <VirtualizedList
                items={receiptRows}
                getKey={(row) => row.key}
                ariaLabel={t("Message details")}
                rowHeight={(row) => (row.kind === "empty" ? ATTACHMENT_ROW_HEIGHT : RECEIPT_ROW_HEIGHT)}
                renderRow={(row) => {
                  if (row.kind === "header") {
                    return (
                      <div className="flex h-full items-end gap-1.5 pb-1.5">
                        <span className={`text-xs font-bold ${row.read ? "text-[#4794BA]" : "text-[#9AA5AD]"}`}>
                          {row.read ? "✓✓" : "✓"}
                        </span>
                        <span className="text-xs font-bold uppercase tracking-[0.08em] text-[#4A545C]">
                          {row.label}
                        </span>
                      </div>
                    );
                  }
                  if (row.kind === "empty") {
                    return (
                      <div className="flex h-full items-center text-xs text-[#9AA5AD]">
                        {t("No one has read this yet.")}
                      </div>
                    );
                  }
                  return (
                    <div className="flex h-full items-center gap-2.5">
                      <UserNameTagComponent showAvatarOnly id={row.userId} size="XS" isCircle />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[0.8125rem] font-semibold text-[#1C2228]">
                          {row.fullName}
                        </span>
                        {row.designation && (
                          <span className="block truncate text-[0.7rem] text-[#6B757D]">{row.designation}</span>
                        )}
                      </span>
                      {row.lastReadDate !== undefined && (
                        <span className="text-[0.7rem] text-[#6B757D]">{messageTime(row.lastReadDate)}</span>
                      )}
                    </div>
                  );
                }}
              />
            )}
          </div>
        </div>
      }
    />
  );
}

/** "View reactions" dialog: per-emoji tabs, remove own only (PRD). */
export function ReactionsDialog({ message, onClose }: { readonly message: ChatMessage; readonly onClose: () => void }) {
  const { t } = useTranslation();
  const chat = usePortalChat();
  // Reactions may change while the dialog is open; read the live copy out of the thread.
  const liveMessage = chat.thread?.messages.find((m) => m._id === message._id) ?? message;
  const reactions = liveMessage.reactions ?? [];
  const [tab, setTab] = useState<string>("all");

  const memberName = (id: string) =>
    chat.thread?.members.find((member) => String(member._id) === id)?.fullName ?? t("Unknown user");

  const rows =
    tab === "all"
      ? reactions.flatMap((reaction) => reaction.users.map((u) => ({ emoji: reaction.emoji, userId: String(u) })))
      : (reactions.find((reaction) => reaction.emoji === tab)?.users ?? []).map((u) => ({
          emoji: tab,
          userId: String(u),
        }));

  const total = reactions.reduce((sum, reaction) => sum + reaction.users.length, 0);

  return (
    <ModalStateDialogComponent
      isVisible
      width="24rem"
      headerTitle={
        <span>
          {t("Reactions")} ({total})
        </span>
      }
      onCancel={onClose}
      footerContent={<></>}
      modelContent={
        <div className="flex w-full flex-col gap-2">
          <div className="rounded-md border border-[#EEF2F5] bg-[#F7F9FB] px-2.5 py-1.5 text-xs">
            <span className="font-semibold">{liveMessage.senderName}: </span>
            <span className="line-clamp-2">
              {liveMessage.text || liveMessage.attachments?.[0]?.attachmentsData.name || t("Attachment")}
            </span>
          </div>
          {!total && <div className="p-3 text-sm text-[#9AA5AD]">{t("No reactions yet")}</div>}
          {total > 0 && (
            <>
              <div className="flex flex-wrap gap-1">
                <button
                  type="button"
                  onClick={() => setTab("all")}
                  className={`rounded-full border px-2 py-0.5 text-xs ${
                    tab === "all" ? "border-[#6BBBE3] bg-[#E4F1F9]" : "border-[#DDE3E8]"
                  }`}
                >
                  {t("All")} {total}
                </button>
                {reactions.map((reaction) => (
                  <button
                    key={reaction.emoji}
                    type="button"
                    onClick={() => setTab(reaction.emoji)}
                    className={`rounded-full border px-2 py-0.5 text-xs ${
                      tab === reaction.emoji ? "border-[#6BBBE3] bg-[#E4F1F9]" : "border-[#DDE3E8]"
                    }`}
                  >
                    {reaction.emoji} {reaction.users.length}
                  </button>
                ))}
              </div>
              <div className="flex flex-col gap-0.5">
                {reactions
                  .filter((reaction) => tab === "all" || reaction.emoji === tab)
                  .map((reaction) => {
                    const names = reaction.users.map((u) =>
                      String(u) === chat.userId ? t("You") : memberName(String(u)),
                    );
                    const last = names.at(-1) ?? "";
                    const sentence = names.length <= 1 ? last : `${names.slice(0, -1).join(", ")} ${t("and")} ${last}`;
                    return (
                      <div key={`sentence-${reaction.emoji}`} className="text-xs text-[#6B757D]">
                        {t("{{names}} reacted with", { names: sentence })} {reaction.emoji}
                      </div>
                    );
                  })}
              </div>
              <div className="h-[35vh]">
                <VirtualizedList
                  items={rows}
                  getKey={(row) => `${row.emoji}-${row.userId}`}
                  rowHeight={ATTACHMENT_ROW_HEIGHT}
                  ariaLabel={t("Reactions")}
                  renderRow={(row) => (
                    <div className="flex h-full items-center justify-between px-1">
                      <span className="flex items-center gap-2 text-sm">
                        <UserNameTagComponent showAvatarOnly id={row.userId} size="XS" isCircle />
                        {row.userId === chat.userId ? t("You") : memberName(row.userId)}
                      </span>
                      <span className="flex items-center gap-2">
                        <span>{row.emoji}</span>
                        {row.userId === chat.userId && (
                          <span
                            role="button"
                            tabIndex={0}
                            title={t("Remove")}
                            onClick={() => void chat.toggleReaction(liveMessage._id, row.emoji)}
                            onKeyDown={() => undefined}
                          >
                            <IconComponent name="CrossIcon" width="0.7rem" height="0.7rem" />
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                />
              </div>
            </>
          )}
        </div>
      }
    />
  );
}
